import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon';
import '../../styles/components/SplitButton.css';

export interface SplitButtonItem {
  key: string;
  label: string;
  icon?: string;
  onClick: () => void;
}

interface SplitButtonProps {
  /** Short text on the main button, e.g. "New". */
  label: string;
  /** Full accessible name and tooltip of the main action, e.g. "New Task". Defaults to label. */
  mainLabel?: string;
  icon?: string;
  /** Filled primary styling, like the other bar create buttons. */
  primary?: boolean;
  /** The main click: the primary create action. */
  onClick: () => void;
  /** The other actions, listed in the caret menu. */
  menu: SplitButtonItem[];
  /** Accessible name of the caret. */
  menuLabel?: string;
}

const OVERFLOW = '.breadcrumb-more-dropdown';

/**
 * One labelled "New" control for a bar that has two or more create actions: the
 * main part runs the primary one, the caret opens a small menu with the rest.
 * Ported from Kalligo's HeaderSplitButton, with keyboard support (ArrowDown on
 * the caret opens, arrows/Home/End move, Esc and Tab close and return focus).
 *
 * The menu is portalled to <body> because the breadcrumb actions row clips its
 * overflow. Inside the phone "more actions" menu there is no room for a caret
 * popup, so it flattens into one full-width labelled row per action.
 */
export const SplitButton: React.FC<SplitButtonProps> = ({
  label, mainLabel, icon, primary, onClick, menu, menuLabel = 'More ways to create',
}) => {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [flat, setFlat] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const caretRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const focusAfterOpen = useRef<'first' | 'last'>('first');

  // Inside the mobile overflow dropdown: flatten (no caret, no popup). The
  // Header renders its actions twice (inline row and this dropdown), each
  // instance mounted where it lives, so checking once at mount is enough.
  useLayoutEffect(() => {
    setFlat(!!rootRef.current?.closest(OVERFLOW));
  }, []);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) caretRef.current?.focus();
  }, []);

  const openMenu = (which: 'first' | 'last') => {
    focusAfterOpen.current = which;
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || caretRef.current?.contains(t)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') { e.preventDefault(); close(true); }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Place under the caret, right-aligned to it, via CSS custom properties
  // (no inline style prop). Reposition on scroll/resize; close if the caret
  // vanished (the bar collapsed into the phone menu).
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = menuRef.current;
      const caret = caretRef.current;
      if (!el || !caret) return;
      const rect = caret.getBoundingClientRect();
      const width = el.offsetWidth || 200;
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      el.style.setProperty('--sb-top', `${rect.bottom + 4}px`);
      el.style.setProperty('--sb-left', `${left}px`);
    };
    place();
    const onResize = () => {
      if (caretRef.current && caretRef.current.getClientRects().length === 0) { setOpen(false); return; }
      place();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const els = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!els || els.length === 0) return;
    (focusAfterOpen.current === 'last' ? els[els.length - 1] : els[0]).focus();
  }, [open]);

  const onCaretKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(e.key === 'ArrowUp' ? 'last' : 'first');
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    const els = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (els.length === 0) return;
    const i = els.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => { e.preventDefault(); els[(n + els.length) % els.length].focus(); };
    switch (e.key) {
      case 'ArrowDown': go(i + 1); break;
      case 'ArrowUp': go(i <= 0 ? els.length - 1 : i - 1); break;
      case 'Home': go(0); break;
      case 'End': go(els.length - 1); break;
      case 'Escape': e.preventDefault(); e.stopPropagation(); close(true); break;
      case 'Tab': close(true); break;
      default: break;
    }
  };

  const tone = primary ? ' primary' : '';
  const name = mainLabel ?? label;

  if (flat) {
    return (
      <div className="split-btn split-btn--flat" ref={rootRef}>
        <button type="button" className={`header-action-btn${tone}`} onClick={onClick}>
          {icon && <Icon name={icon} size={16} />}
          {name}
        </button>
        {menu.map(item => (
          <button key={item.key} type="button" className="header-action-btn" onClick={item.onClick}>
            {item.icon && <Icon name={item.icon} size={16} />}
            {item.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="split-btn" ref={rootRef}>
      <button
        type="button"
        className={`header-action-btn split-btn__main${tone}`}
        onClick={onClick}
        aria-label={name}
        title={name}
      >
        {icon && <Icon name={icon} size={16} />}
        {label}
      </button>
      <button
        type="button"
        ref={caretRef}
        className={`header-action-btn split-btn__caret${tone}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${uid}-menu` : undefined}
        aria-label={menuLabel}
        title={menuLabel}
        onClick={() => (open ? close(false) : openMenu('first'))}
        onKeyDown={onCaretKeyDown}
      >
        <Icon name="chevronDown" size={14} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          id={`${uid}-menu`}
          className="split-btn__menu"
          role="menu"
          aria-label={menuLabel}
          onKeyDown={onMenuKeyDown}
        >
          {menu.map(item => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className="split-btn__item"
              onClick={() => { close(true); item.onClick(); }}
            >
              {item.icon && <Icon name={item.icon} size={16} />}
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SplitButton;
